/**
 * The Striping Plan intersections of a route, as the map tab and the run form see them
 * (spec 2026-10-14 Parts A-C): route order, numbers, default names, on/off the road.
 * Pure.
 */
import { routeTokenFromJsonName } from "../multiRouteRun";
import { laneDenseGeometryLatLng } from "./rsaPathUtils";
import { laneFactsFromFileName } from "./runLabel";
import { EARTH_RADIUS_FT, nearestOnLine } from "./stripingGeometry";
import { listOf, STRIPING_MATCH_FT } from "./stripingList";

const validLatLng = (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng);

/** The route's centerline as [{lat, lng}]: its GeoJSON pathData, else its waypoints. */
export const pathCenterline = (path) => {
  const coords = path?.pathData?.geometry?.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    return coords
      .filter(Array.isArray)
      .map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }))
      .filter(validLatLng);
  }
  return (Array.isArray(path?.waypoints) ? path.waypoints : [])
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter(validLatLng);
};

export const lanesOfPath = (path, lanes) =>
  (lanes || []).filter((l) => l?.pathId === path?.id);

/** Where a point must be to count as on the road: the route's lanes; a route without
 * lanes yet uses its centerline; a route without geometry has none. */
export const laneLinesOfPath = (path, lanes) => {
  const lines = lanesOfPath(path, lanes)
    .map((l) => laneDenseGeometryLatLng(l))
    .filter((line) => line.length >= 2);
  if (lines.length) return lines;
  const center = pathCenterline(path);
  return center.length >= 2 ? [center] : [];
};

export const isNearAnyLine = (point, lines, maxFt = STRIPING_MATCH_FT) =>
  (lines || []).some((line) => {
    const hit = nearestOnLine(point, line);
    return hit != null && hit.distanceFt <= maxFt;
  });

/** The lines' bounding box padded by padFt: {minLat, maxLat, minLng, maxLng}, or null
 * for no lines. A cheap pre-test before the exact nearest-line scan. */
export const linesBounds = (lines, padFt) => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const line of lines || []) {
    for (const p of line) {
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }
  }
  if (!Number.isFinite(minLat)) return null;
  const latPad = padFt / (EARTH_RADIUS_FT * Math.PI / 180);
  const lngPad = latPad / Math.max(0.01, Math.cos(((minLat + maxLat) / 2) * Math.PI / 180));
  return { minLat: minLat - latPad, maxLat: maxLat + latPad, minLng: minLng - lngPad, maxLng: maxLng + lngPad };
};

export const insideBounds = (point, bounds) =>
  Boolean(bounds)
  && point.lat >= bounds.minLat && point.lat <= bounds.maxLat
  && point.lng >= bounds.minLng && point.lng <= bounds.maxLng;

/**
 * The route's list in route order: [{id, lat, lng, name, number, displayName, offRoad}].
 * Ordered by distance along the centerline (stored order without one); `offRoad` when
 * farther than 150 ft from every lane line (never without lines). `lines` may be passed
 * precomputed (laneLinesOfPath).
 */
export const orderedRows = (path, lanes, lines = laneLinesOfPath(path, lanes)) => {
  const center = pathCenterline(path);
  const keyed = listOf(path).map((p, index) => ({
    p,
    index,
    along: center.length >= 2 ? (nearestOnLine(p, center)?.alongFt ?? 0) : index,
  }));
  keyed.sort((a, b) => a.along - b.along || a.index - b.index);
  return keyed.map(({ p }, i) => ({
    ...p,
    number: i + 1,
    displayName: String(p.name ?? "").trim() || `Intersection ${i + 1}`,
    offRoad: lines.length > 0 && !isNearAnyLine(p, lines),
  }));
};

/** The route's list as a run sends it (striping_data.intersections), in route order. */
export const namedIntersections = (path, lanes) =>
  orderedRows(path, lanes).map((r) => ({ lat: r.lat, lng: r.lng, name: r.displayName }));

// ── Roads (how a run pairs the route's lane files) ─────────────────────────────

export const NO_PARTNER = "no partner, will be skipped";
export const NO_LABEL = "no lane label, will be skipped";
export const DUPLICATE_DIRECTION = "two lanes with the same direction, the run will be refused";

/** {pathId: segment}: each route's 1-based ordinal among routes with the same label, in
 * list order — the <segment> of its lanes' PATH_IDs (RSAAnalysis.js
 * saveLasSeparationPathToDrive builds the same counter). */
export const pathSegmentNumbers = (paths) => {
  const counter = {};
  const out = {};
  for (const p of paths || []) {
    const name = String(p?.label ?? "").trim();
    counter[name] = (counter[name] || 0) + 1;
    out[p.id] = counter[name];
  }
  return out;
};

const laneName = (lane) =>
  `${String(lane?.label ?? "").trim() || "(no label)"} ${lane?.lanePosition ?? "LL"}-${lane?.direction ?? "P"}`;

/**
 * The roads the striping plan draws for one route, paired as the run pairs lane files
 * (Backend/Pipeline/utils/striping_roads.py group_roads): an N and a P lane with the
 * same label and lane position. Labels match the check's: `<label>-<segment>-<pos>`.
 */
export const stripingRoadsOfPath = (path, lanes, segment) => {
  const groups = new Map();
  const problems = [];
  for (const lane of lanesOfPath(path, lanes)) {
    const label = String(lane?.label ?? "").trim();
    if (!label) {
      problems.push({ laneId: lane.id, laneName: laneName(lane), reason: NO_LABEL });
      continue;
    }
    const pos = lane?.lanePosition ?? "LL";
    const key = `${label}|${pos}`;
    if (!groups.has(key)) groups.set(key, { label, pos, N: [], P: [] });
    groups.get(key)[(lane?.direction ?? "P") === "N" ? "N" : "P"].push(lane);
  }
  const roads = [];
  for (const g of groups.values()) {
    const all = [...g.N, ...g.P];
    const reason =
      g.N.length > 1 || g.P.length > 1 ? DUPLICATE_DIRECTION
        : !g.N.length || !g.P.length ? NO_PARTNER : null;
    if (reason) {
      all.forEach((l) => problems.push({ laneId: l.id, laneName: laneName(l), reason }));
    } else {
      roads.push({ label: `${g.label}-${segment}-${g.pos}`, nLaneId: g.N[0].id, pLaneId: g.P[0].id });
    }
  }
  return { roads, problems };
};

/**
 * For the run modal: per road label, the route's points within 150 ft of that road's N
 * or P lane; the routes' points near no lane; the total; and each road's route.
 */
export const roadIntersectionCounts = (paths, lanes, pathIds) => {
  const segments = pathSegmentNumbers(paths);
  const out = { counts: {}, pathByRoad: {}, offRoad: 0, total: 0 };
  for (const pathId of pathIds || []) {
    const path = (paths || []).find((p) => p.id === pathId);
    if (!path) continue;
    const list = listOf(path);
    out.total += list.length;
    const lines = laneLinesOfPath(path, lanes);
    if (lines.length) out.offRoad += list.filter((p) => !isNearAnyLine(p, lines)).length;
    const lineOfLane = new Map(lanesOfPath(path, lanes).map((l) => [l.id, laneDenseGeometryLatLng(l)]));
    for (const road of stripingRoadsOfPath(path, lanes, segments[path.id]).roads) {
      const roadLines = [lineOfLane.get(road.nLaneId), lineOfLane.get(road.pLaneId)]
        .filter((line) => line && line.length >= 2);
      out.counts[road.label] = list.filter((p) => isNearAnyLine(p, roadLines)).length;
      out.pathByRoad[road.label] = path.id;
    }
  }
  return out;
};

/** A run's striping_data.intersections: the routes' lists joined, each in route order. */
export const intersectionsForPaths = (paths, lanes, pathIds) =>
  (pathIds || []).flatMap((id) => {
    const path = (paths || []).find((p) => p.id === id);
    return path ? namedIntersections(path, lanes) : [];
  });

// ── Route ↔ path ──────────────────────────────────────────────────────────────

/** The token a route's files carry: LasSeparationPath_<token>.json, <token>_<...>.xml. */
export const pathRouteToken = (path, sanitize) => path?.orthoMapsName || sanitize(path?.label);

export const pathForRouteJsonName = (paths, jsonName, sanitize) => {
  const token = routeTokenFromJsonName(jsonName);
  if (!token) return null;
  return (paths || []).find((p) => pathRouteToken(p, sanitize) === token) || null;
};

/** Route ids (unique, first-seen order) of lane XML files: by xmlToJsonMap, else by the
 * file name's route token. */
export const pathIdsForXmlFiles = (files, { xmlToJsonMap = {}, paths = [], sanitize }) => {
  const ids = [];
  for (const f of files || []) {
    const name = String(f?.name ?? "");
    let id = xmlToJsonMap?.[name]?.pathId;
    if (!paths.some((p) => p.id === id)) {
      const token = laneFactsFromFileName(name).routeToken;
      // Lane XML names carry the route's label (buildLaneXmlFilename), so a renamed
      // route is found by its label first, then by its original orthoMapsName.
      id = token
        ? (paths.find((p) => sanitize(p.label) === token)
          || paths.find((p) => pathRouteToken(p, sanitize) === token))?.id
        : undefined;
    }
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
};

/** The files of a routes-matrix selection ({[normalizedId]: {[task]: bool}}) for a task. */
export const filesAssignedToTask = (files, matrix, task, normalizeId) =>
  (files || []).filter((f) => {
    const key = normalizeId(f?.googleDriveId);
    return Boolean(key && matrix?.[key]?.[task]);
  });
